import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import postureImage from "../../utilities/dummy_assets/image24.png";
import sizeImage from "../../utilities/dummy_assets/image25.png";
import BackgroundImage from "../../assets/background.png";
import { toast } from "react-toastify";

/**
 * RegisterInstructions Component
 * Displays profile photo guidelines before camera capture
 * Shows visual examples of proper posture and framing for profile pictures
 * Provides navigation to camera capture or back to login
 */
const RegisterInstructions = () => {
  const navigate = useNavigate();

  const [isChecked, setIsChecked] = useState(false);
  const [showError, setShowError] = useState(false);

  // Handle "Open Camera" button click
  const handleOpenCamera = async () => {
    if (!isChecked) {
      setShowError(true);
      return;
    }
    setShowError(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      // Stop camera after permission check so it doesn’t stay open on this page
      stream.getTracks().forEach((track) => track.stop());
      navigate("/profile-picture");
    } catch (err) {
      toast.error(
        "Camera access denied. Please allow camera permission to proceed."
      );
    }
  };

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundImage: `url(${BackgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        position: "relative",
        padding: "1rem",
        boxSizing: "border-box",
        flexDirection: "column",
      }}
    >
      {/* Top Right Link */}
      <Box sx={{ position: "absolute", top: 20, right: 20 }}>
        <Typography
          component="a"
          href={process.env.REACT_APP_ADMIN_SITE_BASE_URL}
          sx={{
            textDecoration: "underline",
            color: "#007BFF",
            fontWeight: 500,
            fontSize: "16px",
          }}
        >
          Go to Admin site
        </Typography>
      </Box>

      {/* Main Card */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "520px",
          backgroundColor: "#FFFFFF",
          borderRadius: "24px",
          padding: "1rem",
          boxShadow: "4px 4px 20px rgba(0, 123, 255, 0.1)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "5px",
        }}
      >
        {/* Title */}
        <Typography
          variant="h6"
          fontWeight={600}
          color="#212121"
          align="center"
          sx={{ mb: 1 }}
        >
          Set Your Profile Photo
        </Typography>

        {/* Note */}
        <Box
          sx={{
            width: "100%",
            backgroundColor: "#FEFFD2",
            borderLeft: "4px solid #D0A300",
            borderRadius: "4px",
            padding: "10px 12px",
            textAlign: "center",
          }}
        >
          <Typography fontSize="14px" fontWeight={500} color="#D0A300">
            Keep these things in mind while clicking your picture
          </Typography>
        </Box>

        {/* Posture Section */}
        <Box sx={{ width: "100%" }}>
          <Typography fontWeight={500} fontSize="16px" color="#3F3F3F" mb={1}>
            Posture:
          </Typography>
          <img
            src={postureImage}
            alt="Posture"
            style={{ width: "100%", borderRadius: "6px", objectFit: "contain" }}
          />
        </Box>

        {/* Size Section */}
        <Box sx={{ width: "100%" }}>
          <Typography fontWeight={500} fontSize="16px" color="#3F3F3F" mb={1}>
            Size:
          </Typography>
          <img
            src={sizeImage}
            alt="Size"
            style={{ width: "100%", borderRadius: "6px", objectFit: "contain" }}
          />
        </Box>

        {/* Checkbox Agreement */}
        <Box display="flex" justifyContent="center" width="100%">
          <FormControlLabel
            control={
              <Checkbox
                checked={isChecked}
                onChange={(e) => {
                  setIsChecked(e.target.checked);
                  if (e.target.checked) setShowError(false);
                }}
                sx={{
                  color: "#007BFF",
                  "&.Mui-checked": {
                    color: "#007BFF",
                  },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: "14px", color: "#3F3F3F" }}>
                By using the MBF MIS, you consent to the processing of your
                contact information by Maker Bhavan Foundation for marketing &
                communication purposes. Please confirm that you and read and
                understood the instructions above
              </Typography>
            }
            sx={{
              alignItems: "flex-start",
            }}
          />
        </Box>

        {/* Error Message */}
        {showError && (
          <Alert severity="error" sx={{ width: "100%" }}>
            Please confirm that you have read the instructions before
            proceeding.
          </Alert>
        )}

        {/* Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            mt: 2,
          }}
        >
          <Typography
            sx={{
              color: "#007BFF",
              fontWeight: 500,
              fontSize: "14px",
              cursor: "pointer",
              alignSelf: "center",
            }}
            onClick={() => navigate("/login")}
          >
            &lt; Back
          </Typography>

          <Button
            variant="contained"
            onClick={handleOpenCamera}
            sx={{
              backgroundColor: isChecked ? "#007BFF" : "#ccc",
              color: "#fff",
              fontWeight: 600,
              fontSize: "14px",
              padding: "10px 20px",
              borderRadius: "10px",
              textTransform: "none",
              boxShadow: "none",
              cursor: isChecked ? "pointer" : "not-allowed",
              "&:hover": {
                backgroundColor: isChecked ? "#B2D7FF" : "#ccc",
                boxShadow: "none",
              },
            }}
          >
            Open Camera
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default RegisterInstructions;
